import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InviteAutomationState, SendInviteResponseData, TeamsService } from 'src/app/services/teams/teams.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { SnackBarComponent } from '../snackbar/snackbar.component';

export type SendInvitesScheduleDialogData = {
  teamId: string;
};

export type SendInvitesScheduleDialogResult = {
  invitesSent?: SendInviteResponseData;
  automationUpdated?: boolean;
};

@Component({
  selector: 'app-send-invites-schedule-dialog',
  templateUrl: './send-invites-schedule-dialog.component.html',
  styleUrls: ['./send-invites-schedule-dialog.component.scss'],
})
export class SendInvitesScheduleDialogComponent implements OnInit {
  mode: 'now' | 'scheduled' = 'now';
  scheduledDate: Date | null = null;
  automation: InviteAutomationState | null = null;
  readonly minDate = new Date();
  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SendInvitesScheduleDialogData,
    private ref: MatDialogRef<SendInvitesScheduleDialogComponent, SendInvitesScheduleDialogResult>,
    private teamsService: TeamsService,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadAutomation();
  }

  close(): void {
    this.ref.close();
  }

  get canSubmit(): boolean {
    return !this.isSubmitting && (this.mode === 'now' || (!!this.automation && !!this.scheduledDate));
  }

  get maxDate(): Date | null {
    return this.automation ? this.buildMaxDate(this.automation.initialInviteWaitDays) : null;
  }

  get automationSummary(): string {
    if (!this.automation) {
      return 'Automated invite sending status is unavailable.';
    }
    if (!this.automation.enabled) {
      return 'Automated invite sending is off.';
    }
    if (this.automation.initialInviteSendAt && !this.automation.initialInviteSentAt) {
      return `Initial invites scheduled for ${this.formatDate(this.automation.initialInviteSendAt)}.`;
    }
    if (this.automation.nextRetryAt) {
      return `Retry on ${this.formatDate(this.automation.nextRetryAt)} for ${this.automation.pendingRetryCount || 0} pending signup.`;
    }
    return 'Automated invite sending is on.';
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting = true;
    try {
      if (this.mode === 'now') {
        this.spinnerService.show('Sending invites...');
        const response = await this.teamsService.sendInvites(this.data.teamId);
        if (!response.success) {
          throw new Error(`${response.error || response.message || 'Unable to send invites.'}`);
        }
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: this.mountMessage(response.data),
        });
        this.ref.close({ invitesSent: response.data, automationUpdated: true });
        return;
      }

      const response = await this.teamsService.updateInviteAutomation(this.data.teamId, {
        enabled: true,
        initialInviteSendAt: this.scheduledDate?.toISOString(),
      });
      if (!response.success) {
        throw new Error(`${response.error || response.message || 'Unable to schedule invites.'}`);
      }
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Invites scheduled for ${this.formatDate(response.data.initialInviteSendAt)}.`,
      });
      this.ref.close({ automationUpdated: true });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error updating invites: ${error.message || error.toString()}`,
      });
    } finally {
      this.spinnerService.hide();
      this.isSubmitting = false;
    }
  }

  async disableAutomation(): Promise<void> {
    this.isSubmitting = true;
    try {
      const response = await this.teamsService.updateInviteAutomation(this.data.teamId, {
        enabled: false,
      });
      if (!response.success) {
        throw new Error(`${response.error || response.message || 'Unable to disable automated invites.'}`);
      }
      this.automation = response.data;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Automated invite sending disabled.',
      });
      this.ref.close({ automationUpdated: true });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error disabling invites: ${error.message || error.toString()}`,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  private async loadAutomation(): Promise<void> {
    try {
      const response = await this.teamsService.getInviteAutomation(this.data.teamId);
      if (response.success) {
        this.automation = response.data;
      }
    } catch {
      this.automation = null;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Unable to load automated invite status.',
      });
    }
  }

  private buildMaxDate(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  private formatDate(value: string | null): string {
    if (!value) {
      return '';
    }
    return new Date(value).toLocaleDateString();
  }

  private mountMessage(result: SendInviteResponseData): string {
    const parts: string[] = [];
    if (result.sentCount > 0) {
      parts.push(`${result.sentCount} ${result.sentCount === 1 ? 'invite' : 'invites'} sent.`);
    } else if (result.attemptedCount === 0) {
      parts.push('No pending invites to send.');
    } else {
      parts.push('No invites were sent.');
    }

    if (result.failedCount > 0) {
      parts.push(`${result.failedCount} ${result.failedCount === 1 ? 'invite' : 'invites'} failed.`);
    }

    if (result.warning) {
      parts.push(result.warning);
    }

    return parts.join(' ');
  }
}
